import { TopFeedToggle } from './topFeedToggleContent';
import { debounce } from '@utils/debounce';

/**
 * Funkce pro responzivní přesouvání TopFeed komponenty mezi desktop/mobil verzí.
 * Element se fyzicky přesune v DOMu, takže se zachovají event listenery a vnitřní stav.
 *
 * Používají se tyto data atributy:
 *   - [data-top-feed-element]  → samotný prvek (obal), který se má přesouvat
 *   - [data-top-feed-desktop]  → cílový kontejner pro desktop verzi (šířka >= breakpoint)
 *   - [data-top-feed-mobile]   → cílový kontejner pro mobilní verzi (šířka < breakpoint)
 *
 * Breakpoint je šířka obrazovky (px), od které se zobrazuje desktopová verze, má výchozí hodnotu 679.
 */
export const initTopFeedResponsive = (breakpoint: number = 679): void => {
  const topFeedElement = document.querySelector('[data-top-feed-element]') as HTMLElement;
  if (!topFeedElement) {
    console.warn('Nepodarilo sa nájsť element s atribútom data-top-feed-element');
    return;
  }

  // Cílové kontejnery:
  //  - Desktop: prvek s atributem [data-top-feed-desktop]
  //  - Mobil: prvek s atributem [data-top-feed-mobile]
  const desktopContainer = document.querySelector('[data-top-feed-desktop]') as HTMLElement;
  const mobileContainer = document.querySelector('[data-top-feed-mobile]') as HTMLElement;

  if (!desktopContainer || !mobileContainer) {
    console.warn('Nepodarilo sa nájsť jeden z cieľových kontajnerov');
    return;
  }

  // Instance TopFeedToggle (pro reinicializaci po přesunu)
  let topFeedToggleInstance: ReturnType<typeof TopFeedToggle> | null = null;

  /**
   * Inicializuje TopFeedToggle uvnitř přesunutého elementu.
   * Pokud už existuje předchozí instance, nejdříve ji zruší.
   */
  const initializeTopFeedToggle = (): void => {
    if (topFeedToggleInstance) {
      // Vyčištění starých listenerů a stavu
      topFeedToggleInstance.cleanup();
      topFeedToggleInstance = null;
    }

    const containerElement = topFeedElement.querySelector('[data-top-feed-toggle]') as HTMLElement;
    if (containerElement) {
      // Nová instance
      topFeedToggleInstance = TopFeedToggle({ containerElement });
    }
  };

  /**
   * Přesun TopFeed elementu do správného kontejneru
   * podle aktuální šířky viewportu.
   */
  const moveElement = (): void => {
    const isDesktop = window.innerWidth >= breakpoint;
    const targetContainer = isDesktop ? desktopContainer : mobileContainer;

    if (topFeedElement.parentElement !== targetContainer) {
      targetContainer.appendChild(topFeedElement);
      initializeTopFeedToggle();
    }
  };

  // Počáteční inicializace
  moveElement();

  // Reinit na změnu velikosti
  window.addEventListener('resize', debounce(moveElement, 250));
};
